/**
 * Named pure guards (PROJECT_SPEC.md §5.1 rules engine; WF-003/007/011/014/015/016/022, ESIG-021, N9).
 * Each guard reads FACTS the server loaded (never the database directly) so the identical function runs in
 * the offline client for optimistic checks. A missing fact fails closed.
 */
import type { Guard, GuardVerdict } from "./machine.js";

const fail = (code: string, message: string): GuardVerdict => ({ ok: false, code, message });
const ok: GuardVerdict = { ok: true };

function bool(facts: Readonly<Record<string, unknown>>, key: string): boolean | null {
  const v = facts[key];
  return typeof v === "boolean" ? v : null;
}

/** Builds a guard that requires fact `key` to be exactly true (unknown → fail closed). */
function requireTrue(key: string, code: string, message: string): Guard {
  return (facts) => {
    const v = bool(facts, key);
    if (v === null)
      return fail(`${code}_UNKNOWN`, `${message} (fact "${key}" not provided — refusing, fail closed)`);
    return v ? ok : fail(code, message);
  };
}

export const GUARDS: Readonly<Record<string, Guard>> = {
  /** WF-003: all prerequisite steps (per the MBR procedure graph) are COMPLETE or legitimately SKIPPED. */
  prereqStepsComplete: requireTrue(
    "prereqStepsComplete",
    "PREREQ_INCOMPLETE",
    "prerequisite steps are not complete (21 CFR 11.10(f))",
  ),
  /** WF-007: mandatory steps cannot be skipped. */
  stepIsOptional: requireTrue("stepIsOptional", "MANDATORY_STEP", "a mandatory step cannot be skipped"),
  /** WF-015: equipment is clean (CHT not expired), calibrated and qualified. */
  equipmentReady: requireTrue(
    "equipmentReady",
    "EQUIPMENT_NOT_READY",
    "equipment is not clean/calibrated/qualified for use (21 CFR 211.67/211.68)",
  ),
  /** WF-014: material lot RELEASED and not expired/retest-due at point of use. */
  lotReleasedAndUnexpired: requireTrue(
    "lotReleasedAndUnexpired",
    "LOT_NOT_USABLE",
    "material lot is not released or has expired (21 CFR 211.84)",
  ),
  /** WF-011/012: no open deviation blocks progression / release. */
  noOpenDeviation: requireTrue(
    "noOpenDeviation",
    "OPEN_DEVIATION",
    "an open deviation blocks this transition",
  ),
  /** WF-012: no unreviewed audit-trail items (draft Annex 11 §12.8) and no open exceptions. */
  reviewComplete: requireTrue(
    "reviewComplete",
    "REVIEW_INCOMPLETE",
    "review is incomplete: open exceptions or unreviewed audit-trail items remain",
  ),
  /** WF-016 / ESIG-029: operator's training on the governing SOP version is current. */
  trainingCurrent: requireTrue(
    "trainingCurrent",
    "TRAINING_NOT_CURRENT",
    "operator training on the governing SOP version is not current (21 CFR 11.10(i))",
  ),
  /** WF-022: min hold elapsed and max hold not exceeded. */
  holdTimeWithinWindow: requireTrue(
    "holdTimeWithinWindow",
    "HOLD_TIME_VIOLATION",
    "hold-time window violated (below minimum or above maximum)",
  ),
  /** WF-018 / SEC-021 / N9: the checker/approver is not the author. */
  authorNotChecker: requireTrue(
    "authorNotChecker",
    "AUTHOR_IS_CHECKER",
    "author and checker must be different people (21 CFR 211.186(a))",
  ),
  /** ESIG-021: verifier is not the performer. */
  verifierNotPerformer: requireTrue(
    "verifierNotPerformer",
    "VERIFIER_IS_PERFORMER",
    "verifier and performer must be different people (21 CFR 211.101(c))",
  ),
  /** WF-019: only an EFFECTIVE MBR version may start a batch. */
  mbrEffective: requireTrue(
    "mbrEffective",
    "MBR_NOT_EFFECTIVE",
    "an unapproved or non-effective MBR cannot be used to start a batch",
  ),
  /** WF-021: batch number unique. */
  batchNumberUnique: requireTrue(
    "batchNumberUnique",
    "DUPLICATE_BATCH_NUMBER",
    "a batch with this number already exists",
  ),
  /** WF-013 / ESIG-022: all required signatures present before release. */
  allSignaturesPresent: requireTrue(
    "allSignaturesPresent",
    "SIGNATURES_MISSING",
    "required signatures are missing",
  ),
  /** WF-012: no failing / unapproved LIMS result. */
  labResultsApproved: requireTrue(
    "labResultsApproved",
    "LAB_BLOCKER",
    "a failing or unapproved laboratory result blocks release",
  ),
  /** WF-028: review-by-exception release requires market config + validated & continuously monitored process. */
  rbePermitted: requireTrue(
    "rbePermitted",
    "RBE_NOT_PERMITTED",
    "review-by-exception is not permitted for this market/process; full review is required",
  ),
  /** WF-005: all mandatory parallel branches have converged. */
  parallelBranchesJoined: requireTrue(
    "parallelBranchesJoined",
    "BRANCHES_OPEN",
    "not all mandatory parallel branches have completed",
  ),
  /** ISA-88: a phase may complete only on a real process condition, never merely a timer (§4.1). */
  processConditionMet: requireTrue(
    "processConditionMet",
    "PROCESS_CONDITION_NOT_MET",
    "phase completion requires the process end-condition, not just elapsed time",
  ),
  /** URS-EXE-023: the scale's daily sensitivity/accuracy check passed today and blocks dispensing until it has. */
  scaleCheckCurrent: requireTrue(
    "scaleCheckCurrent",
    "SCALE_CHECK_DUE",
    "the scale's daily performance check has not passed today (21 CFR 211.68(a))",
  ),
  /** WF-014: the lot is RELEASED, unexpired and not past retest, re-checked at the point of use. */
  materialReleased: requireTrue(
    "materialReleased",
    "MATERIAL_NOT_RELEASED",
    "the material lot is not released, or is expired or quarantined (21 CFR 211.84)",
  ),
  /** URS-EXE-024: the weighed quantity is inside the hard tolerance band. */
  withinDispenseTolerance: requireTrue(
    "withinDispenseTolerance",
    "TOLERANCE_EXCEEDED",
    "the dispensed quantity is outside the hard tolerance band",
  ),
  /** URS-EXE-025: every container produced by this order carries a printed label. */
  allContainersLabelled: requireTrue(
    "allContainersLabelled",
    "CONTAINER_UNLABELLED",
    "every dispensed container must carry a printed label before the order can complete",
  ),
  /** URS-MD-011: a unit returns to service only with calibration and qualification both in date. */
  calibrationAndQualificationValid: requireTrue(
    "calibrationAndQualificationValid",
    "CALIBRATION_OR_QUALIFICATION_EXPIRED",
    "calibration or qualification is not in date (21 CFR 211.68, Annex 15)",
  ),
  /** WF-010: void requires a reason (non-empty) — the reason itself is captured by the audit trail. */
  reasonProvided: (facts) =>
    typeof facts["reason"] === "string" && facts["reason"].trim().length > 0
      ? ok
      : fail("REASON_REQUIRED", "a reason is required"),
};

export type GuardName = keyof typeof GUARDS;
